import React, { useState, useEffect, useCallback } from 'react'
import { Typography, Input, Button, Spinner, IconButton } from '@material-tailwind/react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { PiEyeBold, PiEyeSlashBold } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { logger } from '../utils/helpers/logger'
import { signInWithEmailAndPassword } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from '../contexts/SessionContext'
import { useGeneralToast } from '../hooks/toast'
import { usePageTitle } from '../hooks/toolkit'
import Container from '../components/Container'
import { useDarkMode } from '../contexts/DarkModeContext'
import SessionExpiredDialog from '../components/SessionExpiredDialog'
import { getNextPath } from '../utils/helpers/signin-redirect.helpers'

const SESSION_EXPIRED_PARAM = 'session_expired'

const getSearchParams = (search: string) => {
  if (!search) return null
  try {
    const normalizedSearch = search.startsWith('?') ? search.substring(1) : search
    return new URLSearchParams(normalizedSearch)
  } catch (error) {
    logger.error(error, 'UserSignin')
    return null
  }
}

const hasSessionExpiredFlag = (search: string) => {
  const params = getSearchParams(search)
  if (!params) return false
  return params.get(SESSION_EXPIRED_PARAM) === 'true'
}

const removeSessionExpiredFlag = (search: string) => {
  const params = getSearchParams(search)
  if (!params) return ''
  params.delete(SESSION_EXPIRED_PARAM)
  return params.toString()
}

const UserSignin = () => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  usePageTitle('Signin | Agami')

  const { error: toastError } = useGeneralToast()
  const { user, refresh } = useSessionContext()

  const location = useLocation()
  const navigate = useNavigate()
  const { pathname, search, hash } = location

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false)

  useEffect(() => {
    setSessionExpiredModalOpen(hasSessionExpiredFlag(search))
  }, [search])

  const handleSessionExpiredDismiss = useCallback(() => {
    setSessionExpiredModalOpen(false)
    try {
      const nextSearch = removeSessionExpiredFlag(search)
      navigate(pathname + (nextSearch ? `?${nextSearch}` : ''), { replace: true })
    } catch (error) {
      logger.error(error, 'UserSignin')
    }
  }, [navigate, pathname, search, hash])

  if (user) {
    // send a logged user to its dashboard
    return <Navigate to={getNextPath(search) ?? '/dashboard'} state={{ from: location }} replace />
  }

  const login = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (!email || !password) {
      return
    }

    setLoading(true)

    const { error } = await signInWithEmailAndPassword({ email, password })

    if (error) {
      toastError(t('auth.wrongCredentials'))
      setLoading(false)
      return
    }

    setLoading(false)
    refresh(getNextPath(search) ?? '/')
  }

  return (
    <>
      <SessionExpiredDialog open={sessionExpiredModalOpen} onClose={handleSessionExpiredDismiss} />
      <Container direction="col">
        <Typography className="mt-4 mb-12" variant="h1">
          {t('auth.signIn')}
        </Typography>

        <form className="w-full md:w-1/2 flex flex-col gap-8" onSubmit={login}>
          <Input
            {...(darkMode && { color: 'white' })}
            value={email || ''}
            onChange={(e) => setEmail(e.currentTarget.value)}
            type="email"
            label={t('auth.emailAddress')}
            required
            data-testid="signin-email-input"
          />
          <div className="relative flex w-full">
            <Input
              className="pr-20"
              {...(darkMode && { color: 'white' })}
              value={password || ''}
              onChange={(e) => setPassword(e.currentTarget.value)}
              type={showPassword ? 'text' : 'password'}
              label={t('auth.password')}
              required
              containerProps={{
                className: 'min-w-0',
              }}
              data-testid="signin-password-input"
            />
            <IconButton
              size="sm"
              variant="text"
              className="!absolute right-1 top-1 rounded dark:text-base-gray-500"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <PiEyeSlashBold /> : <PiEyeBold />}
            </IconButton>
          </div>
          <Button type="submit" disabled={loading} data-testid="signin-confirm-btn">
            {loading ? <Spinner className="h-4 w-4 mx-auto" /> : t('auth.signIn')}
          </Button>
        </form>

        <div className="my-6">
          <Link to="/reset-password">
            <Button color={(darkMode ? 'accent' : 'primary') as colors} variant="text">
              {t('auth.forgotPassword')}
            </Button>
          </Link>
        </div>

        <hr className="w-full md:w-1/2 my-6 opacity-20 border-primary dark:border-white" />

        <div className="flex w-full justify-center items-center gap-2 my-2">
          <Typography>{t('auth.noHeadphonesYet')}</Typography>
          <Link
            to="https://www.indiegogo.com/projects/agami-everywhere-immersive-sound"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography className="underline font-bold">{t('auth.buyNow')}</Typography>
          </Link>
        </div>

        <div className="flex w-full justify-center items-center gap-2 my-2">
          <Typography>{t('auth.noAccountYet')}</Typography>
          <Link to="/register">
            <Typography className="underline font-bold">{t('auth.createOne')}</Typography>
          </Link>
        </div>
      </Container>
    </>
  )
}

export default UserSignin
